import { PHONE_COUNTRY_CODE } from "../../LoginUser/hook/phoneCountryCode";
import { normalizeCountryKey, toCanonicalCountryName } from "../../CountryRates/countryCatalog";
import { parseMoneyToNumber } from "./moneyInput";

const DEFAULT_STT_USD = 2.25;

export type PartnerPricingPreviewInput = {
	countryRatesRows?: any[];
	address?: string | null;
	addressText?: string | null;
	selectedCountryCode?: string | null;
	destinationCountry?: string | null;
	localCurrencyCode?: string | null;
	manualStationCom?: string | null;
	manualMinimumPayout?: string | null;
	manualPubRate?: string | null;
};

export type PartnerPricingPreview = {
	matchedRow: any | null;
	detectedCountry: string | null;
	detectedCurrency: { code: string; name: string } | null;
	wiseRate: number | null;
	estSttCom: number | null;
	estMinPayout: number | null;
	estPubRate: number | null;
	appliedStationCom: number | null;
	appliedMinimumPayoutLocal: number | null;
	appliedMinimumPayoutUsd: number | null;
	appliedPubRate: number | null;
};

function toFiniteNumber(value: any) {
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
}

function toPositiveNumber(value: any) {
	const num = toFiniteNumber(value);
	return num && num > 0 ? num : null;
}

function readValue(row: any, keys: string[]) {
	for (const key of keys) {
		if (row?.[key] != null && row?.[key] !== "") {
			return row[key];
		}
	}
	return null;
}

function matchRowByCountry(countryName: any, rows: any[]) {
	const key = normalizeCountryKey(countryName);
	if (!key) return null;

	return (
		rows.find((row) => normalizeCountryKey(row?.country ?? row?.Country) === key) ?? null
	);
}

function matchCountryFromText(text: string, rows: any[]) {
	const normalizedText = String(text || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();

	if (!normalizedText.trim()) return null;

	const normalizedRows = rows
		.map((row) => ({
			row,
			country: String(row?.country ?? row?.Country ?? "").trim(),
		}))
		.filter((item) => item.country)
		.sort((a, b) => b.country.length - a.country.length);

	for (const item of normalizedRows) {
		const countryText = item.country
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.toLowerCase();
		if (countryText && normalizedText.includes(countryText)) {
			return item.row;
		}
	}

	return null;
}

function matchCountryFromPhone(selectedCountryCode: any, rows: any[]) {
	if (!selectedCountryCode) return null;

	const phoneEntry = PHONE_COUNTRY_CODE.find((p) => p.dial_code === selectedCountryCode);
	if (!phoneEntry?.name) return null;

	return matchRowByCountry(phoneEntry.name, rows);
}

export function buildPartnerPricingPreview(
	input: PartnerPricingPreviewInput
): PartnerPricingPreview {
	const rows = Array.isArray(input.countryRatesRows) ? input.countryRatesRows : [];
	const destinationMatch = matchRowByCountry(input.destinationCountry, rows);
	const textMatch = matchCountryFromText(
		`${input.address ?? ""} ${input.addressText ?? ""}`,
		rows
	);
	const phoneMatch = matchCountryFromPhone(input.selectedCountryCode, rows);

	const matchedRow = destinationMatch || textMatch || phoneMatch || null;
	const detectedCountry =
		toCanonicalCountryName(destinationMatch?.country ?? destinationMatch?.Country ?? "") ||
		toCanonicalCountryName(input.destinationCountry ?? "") ||
		toCanonicalCountryName(textMatch?.country ?? textMatch?.Country ?? "") ||
		toCanonicalCountryName(phoneMatch?.country ?? phoneMatch?.Country ?? "") ||
		null;

	const wiseRate = toPositiveNumber(readValue(matchedRow, ["wiseRate", "Wise Rate", "WiseRate"]));
	const countryRateStationCom =
		toPositiveNumber(readValue(matchedRow, ["stationCom", "Station Com", "Station Commission"])) ??
		(wiseRate ? Math.round(wiseRate * DEFAULT_STT_USD) : null);
	const estMinPayout = toPositiveNumber(
		readValue(matchedRow, [
			"minPayingCap",
			"Min Paying Cap",
			"minimumPayout",
			"Minimum Payout",
			"Min Payout",
		])
	);
	const estPubRate = toPositiveNumber(
		readValue(matchedRow, ["pubRate", "Pub.Rate (USD/EUR)", "Pub Rate", "PubRate"])
	);
	const manualStationCom = parseMoneyToNumber(input.manualStationCom);
	const manualMinimumPayoutUsd = parseMoneyToNumber(input.manualMinimumPayout);
	const manualPubRate = parseMoneyToNumber(input.manualPubRate);

	const currencyCode =
		String(readValue(matchedRow, ["code", "Code"]) ?? "").trim().toUpperCase() ||
		String(input.localCurrencyCode ?? "").trim().toUpperCase();
	const currencyName = String(readValue(matchedRow, ["currency", "Currency"]) ?? "").trim();

	const appliedMinimumPayoutLocal = manualMinimumPayoutUsd
		? currencyCode && currencyCode !== "USD" && wiseRate
			? Math.round(manualMinimumPayoutUsd * wiseRate)
			: manualMinimumPayoutUsd
		: estMinPayout;

	return {
		matchedRow,
		detectedCountry: detectedCountry || null,
		detectedCurrency: currencyCode
			? {
				code: currencyCode,
				name: currencyName,
			}
			: null,
		wiseRate,
		estSttCom: countryRateStationCom,
		estMinPayout,
		estPubRate,
		appliedStationCom: manualStationCom ?? countryRateStationCom,
		appliedMinimumPayoutLocal,
		appliedMinimumPayoutUsd: manualMinimumPayoutUsd,
		appliedPubRate: manualPubRate ?? estPubRate,
	};
}

function roundMoney(value: number) {
	return Math.round(value * 1000) / 1000;
}

export function validatePartnerPriceComposition(
	price?: string | null,
	insurancePrice?: string | null,
	publicRate?: string | null
) {
	const fee = parseMoneyToNumber(price);
	const insurance = parseMoneyToNumber(insurancePrice);
	const pubRate = parseMoneyToNumber(publicRate);

	if (fee === null || insurance === null || pubRate === null) {
		return {
			isValid: false,
			total: null as number | null,
		};
	}

	const total = roundMoney(fee + insurance);
	return {
		isValid: Math.abs(total - roundMoney(pubRate)) < 0.0001,
		total,
	};
}
